/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import apiClient from '../../../services/api-client';
import { RootState } from '../../../store/rootReducer';
import AuthSlice, { getProperty } from '../../azure-b2c/storage';
import {
  GetAgreementsReducerAction,
  GetOrdersReducerAction,
  SalesAgreementApiResponse,
  SalesOrderApiResponse,
  SalesState,
} from './types';
import dayjs from 'dayjs';
import { transformSalesAgreements } from './utils';
import { storageDb } from '../../../services/localforage';

export const getSalesAgreements = createAsyncThunk<GetAgreementsReducerAction, string>(
  'sales/agreements',
  async (accountNumber) => {
    const request = await apiClient.get<SalesAgreementApiResponse, AxiosError>(
      `/sales-agreements?accountNumber=${accountNumber}`,
    );

    return {
      data: transformSalesAgreements(request.data.salesAgreements, accountNumber),
      accountNumber: accountNumber,
    };
  },
);

export const getSalesOrders = createAsyncThunk<GetOrdersReducerAction, string>(
  'sales/orders',
  async (agreementId) => {
    const request = await apiClient.get<SalesOrderApiResponse, AxiosError>(
      `/sales-orders?agreementId=${agreementId}`,
    );

    return {
      data: request.data.orders,
      agreementId,
    };
  },
);

export const salesPersistConfig = {
  key: 'sales',
  storage: storageDb,
  // serialize: false,
  blacklist: ['status'],
};

export const initialState: SalesState = {
  agreements: {},
  agreementsStatus: {},
  orders: {},
  orderStatus: {},
};

const SalesSlice = createSlice({
  name: 'products',
  initialState: initialState,
  extraReducers: (builder) => {
    // Agreements
    builder.addCase(AuthSlice.actions.setSelectedFarmerAccount, (state, { payload }) => {
      state.agreementsStatus[payload] = 'idle';
    });
    builder.addCase(getSalesAgreements.fulfilled, (state, action) => {
      state.agreements[action.payload.accountNumber] = action.payload.data;
      state.agreementsStatus[action.payload.accountNumber] = 'fulfilled';
    });
    builder.addCase(getSalesAgreements.pending, (state, { meta }) => {
      state.agreementsStatus[meta.arg] = 'pending';
    });
    builder.addCase(getSalesAgreements.rejected, (state, { meta }) => {
      state.agreementsStatus[meta.arg] = 'fulfilled';
    });

    builder.addCase(getSalesOrders.fulfilled, (state, action) => {
      state.orders[action.payload.agreementId] = action.payload.data;
      state.orderStatus[action.payload.agreementId] = 'fulfilled';
    });
    builder.addCase(getSalesOrders.pending, (state, { meta }) => {
      state.orderStatus[meta.arg] = 'pending';
    });
    builder.addCase(getSalesOrders.rejected, (state, { meta }) => {
      state.orderStatus[meta.arg] = 'fulfilled';
    });
  },
  reducers: {},
});

const selectState = (state: RootState) => state;

export const selectAgreementsRequestStatus = createSelector(selectState, (state) => {
  const propertyId = getProperty(state);
  if (propertyId && Object.keys(state.sales.agreementsStatus).indexOf(propertyId) !== -1) {
    return state.sales.agreementsStatus[propertyId];
  }

  return 'idle';
});

export const selectAllSalesAgreements = createSelector(selectState, (state) => {
  const propertyId = getProperty(state);

  if (propertyId && Object.keys(state.sales.agreements).indexOf(propertyId) !== -1) {
    return state.sales.agreements[propertyId];
  }

  return [];
});

export const selectActiveSalesAgreements = createSelector(selectState, (state) => {
  const propertyId = getProperty(state);

  if (propertyId && Object.keys(state.sales.agreements).indexOf(propertyId) !== -1) {
    const agreements = state.sales.agreements[propertyId];
    const arr = Object.keys(agreements).map((e) => agreements[e]);

    const filtered = arr
      .filter((x) => x.status !== 'Closed')
      .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());

    return filtered;
  }

  return [];
});

export const selectUpcomingSalesAgreements = createSelector(selectState, (state) => {
  const propertyId = getProperty(state);

  if (propertyId && Object.keys(state.sales.agreements).indexOf(propertyId) !== -1) {
    const agreements = state.sales.agreements[propertyId];
    const arr = Object.keys(agreements).map((e) => agreements[e]);

    const comparisonDate = dayjs(Date.now()).add(1, 'day');

    const filtered = arr
      .filter((x) => x.status !== 'Closed' && dayjs(x.startDate).isAfter(comparisonDate))
      .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());

    return filtered;
  }

  return undefined;
});

export const selectPastSalesAgreements = createSelector(selectState, (state) => {
  const propertyId = getProperty(state);

  if (propertyId && Object.keys(state.sales.agreements).indexOf(propertyId) !== -1) {
    const agreements = state.sales.agreements[propertyId];
    const arr = Object.keys(agreements).map((e) => agreements[e]);

    const filtered = arr
      .filter((x) => x.status === 'Closed')
      .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());

    return filtered;
  }

  return undefined;
});

export const selectSalesAgreementById = (id: string | undefined) => {
  return createSelector(selectState, (state) => {
    if (id) {
      const propertyId = getProperty(state);

      if (propertyId && Object.keys(state.sales.agreements).indexOf(propertyId) !== -1) {
        return state.sales.agreements[propertyId][id];
      }
    }

    return undefined;
  });
};

export const selectSalesOrderRequestStatusByAgreementId = (id: string | undefined) => {
  return createSelector(selectState, (state) => {
    if (id && Object.keys(state.sales.orderStatus).indexOf(id) !== -1) {
      return state.sales.orderStatus[id];
    }
    return 'idle';
  });
};

export const selectSalesOrderByAgreementId = (id: string | undefined) => {
  return createSelector(selectState, (state) => {
    if (id && Object.keys(state.sales.orders).indexOf(id) !== -1) {
      return [...state.sales.orders[id]].sort((a, b) => b.id.localeCompare(a.id));
    }

    return undefined;
  });
};

export type { SalesState } from './types';
export default SalesSlice;
